import { QueryClient, useQuery } from "@tanstack/react-query";
import { API_ROUTES } from "definitions/constants/routeConstants";
import apiClient from "services/ApiClient";
import { createQueryString } from "helpers/queryString";
import { IssueDetail, IssueId, IssueSummary } from "models/issue";
import { newHireJourneyKeys } from "features/NewHireJourney/hooks";
import { NewHireJourney, NewHireJourneyId } from "models/newHire";
import { apiNewHireWithActiveIssuesListPath, resourceApiPathByPrefixId } from "helpers/url/api/v2";
import { ActionV2, PrefixId } from "features/Resources/Shared/models";
import { sharedResourceKeys } from "features/Resources/Shared/hooks";
import { emailKeys } from "features/Resources/Email/hooks";
import { messageKeys } from "features/Resources/Message/hooks";
import { taskKeys } from "features/Resources/Task/hooks";
import { apiIssuePathById } from "helpers/url/pages/v2/issuepath";
import { actionIdObjectFromPrefixId } from "features/PendingNewHire/helpers";
import { fromActionPrefixId } from "features/Resources/Shared/helpers";
import { generatePath } from "react-router";
import actionIssuesResolvedBatcher from "./batcher";
import issuesByJourneyBatcher from "./issuesByJourneyBatcher";
import { ActionIssuesResolvedResponse } from "./models";

export type { ActionIssuesResolvedResponse };

export const issueKeys = {
  all: ["issues"] as const,
  lists: () => [...issueKeys.all, "list"] as const,
  list: (filters: string) => [...issueKeys.lists(), ...filters] as const,
  details: () => [...issueKeys.all, "detail"] as const,
  detail: (id: IssueId) => [...issueKeys.details(), id] as const,
  byActions: () => [...issueKeys.all, "actions"] as const,
  byActionPrefixId: (actionPrefixId: string) => [...issueKeys.byActions(), actionPrefixId] as const,
  resolvedByActionPrefixId: (actionPrefixId: string) => [...issueKeys.byActionPrefixId(actionPrefixId), "resolved"] as const,
  counts: () => [...issueKeys.all, "count"] as const,
  count: (issueTypes?: string[]) => [...issueKeys.counts(), { issueTypes }] as const,
  resolved: () => [...issueKeys.all, "resolved"] as const,
  resolvedById: (issueId: IssueId) => [...issueKeys.resolved(), issueId] as const,
  actionResolved: (issueId: IssueId, actionPrefixId: PrefixId) => [...issueKeys.resolvedById(issueId), "action", actionPrefixId] as const,
  issueActions: () => [...issueKeys.all, "issueActions"] as const,
  issueActionsById: (issueId: IssueId) => [...issueKeys.issueActions(), issueId] as const,
  byNewHireJourneys: () => [...issueKeys.lists(), "newHireJourneys"] as const,
  byNewHireJourneyId: (newHireJourneyId: NewHireJourneyId) => [...issueKeys.byNewHireJourneys(), newHireJourneyId] as const,
  newHiresWithIssues: () => [...issueKeys.all, "newHiresWithIssues"] as const,
  pendingIssuesByActions: () => [...issueKeys.all, "pendingIssuesByActions"] as const,
  pendingIssuesByActionId: (actionId: ActionV2["id"]) => [...issueKeys.pendingIssuesByActions(), actionId] as const,
};

export function clearIssueQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: issueKeys.lists() });
  queryClient.invalidateQueries({ queryKey: issueKeys.details() });
  queryClient.invalidateQueries({ queryKey: issueKeys.byActions() });
  queryClient.invalidateQueries({ queryKey: emailKeys.details() });
  queryClient.invalidateQueries({ queryKey: messageKeys.details() });
  queryClient.invalidateQueries({ queryKey: taskKeys.details() });
  queryClient.invalidateQueries({ queryKey: sharedResourceKeys.lists() });
  queryClient.invalidateQueries({ queryKey: newHireJourneyKeys.details() });
}

export const useIssueCountQuery = (issueTypes?: string[], enabled: boolean = true, options: { enabled?: boolean } = {}) => {
  const queryString = issueTypes && issueTypes.length > 0
    ? `?${issueTypes.map((type) => `issue_types[]=${type}`).join("&")}`
    : "";

  return useQuery<{ count: number }>({
    queryKey: issueKeys.count(issueTypes),
    queryFn: async () => apiClient.get(`${API_ROUTES.issues.count}${queryString}`),
    enabled,
    ...options,
  });
};

export const useIssueDetailQuery = (id: IssueId | undefined, options: { enabled?: boolean } = {}) => {
  const mergeEnabled = options?.enabled !== undefined ? options.enabled && !!id : !!id;
  return useQuery<IssueDetail>({
    queryKey: id ? issueKeys.detail(id) : [],
    queryFn: async () => apiClient.get(apiIssuePathById(id)),
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
    enabled: mergeEnabled,
  });
};

export const useIssuesByNewHireIdQuery = (newHireJourneyId: NewHireJourneyId = "", options: { enabled?: boolean } = {}) => {
  const mergeEnabled = options?.enabled !== undefined ? options.enabled && !!newHireJourneyId : !!newHireJourneyId;
  return useQuery<IssueSummary[]>({
    queryKey: issueKeys.byNewHireJourneyId(newHireJourneyId),
    queryFn: async () => issuesByJourneyBatcher.fetch({ newHireJourneyId }),
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
    enabled: mergeEnabled,
  });
};

export function invalidateIssueQueries(queryClient: QueryClient, issue: IssueSummary | IssueDetail) {
  const queryString = createQueryString({ filters: [{ fieldName: "issues.newHireJourneyId", value: issue?.newHireJourneyId, operator: "=" }] });
  queryClient.invalidateQueries({ queryKey: issueKeys.list(queryString) });
  queryClient.invalidateQueries({ queryKey: newHireJourneyKeys.detail(issue?.newHireJourneyId) });
  queryClient.invalidateQueries({ queryKey: issueKeys.detail(issue?.newHireJourneyId) });
  queryClient.invalidateQueries({ queryKey: newHireJourneyKeys.withActiveIssuesList() });
}

export function updateIssueActionList({ queryClient, issueId, action }: { issueId: IssueId, action: ActionV2, queryClient: QueryClient }): void {
  queryClient.setQueryData<ActionV2[]>(issueKeys.issueActionsById(issueId), (prevActions) => {
    if (!prevActions) return prevActions;

    return prevActions.map((oldAction) => {
      if (oldAction.prefixId !== action?.prefixId) return oldAction;

      return {
        ...oldAction,
        ...action,
      };
    });
  });
}

export function useNewHiresWithIssuesListQuery(options: { enabled?: boolean } = {}) {
  return useQuery<NewHireJourney[]>({
    queryKey: issueKeys.newHiresWithIssues(),
    queryFn: async () => apiClient.get(apiNewHireWithActiveIssuesListPath()),
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
  });
}

export const useIssuesByActionPrefixIdQuery = (prefixId: PrefixId, options: { enabled?: boolean } = {}) => useQuery<IssueSummary[]>({
  queryKey: issueKeys.byActionPrefixId(prefixId),
  queryFn: async () => apiClient.get(`${resourceApiPathByPrefixId(prefixId)}/issues`),
  staleTime: 1000 * 60 * 3, // 3 minutes
  ...options,
});

export const usePendingIssuesByActionPrefixIdQuery = (prefixId: PrefixId, options: { enabled?: boolean } = {}) => {
  const mergeEnabled = options?.enabled !== undefined ? options.enabled && !!prefixId : !!prefixId;
  const actionArgs = actionIdObjectFromPrefixId(prefixId);
  const { id } = fromActionPrefixId(prefixId);
  const query = createQueryString({ additionalQueries: actionArgs });
  return useQuery<IssueSummary[]>({
    queryKey: issueKeys.pendingIssuesByActionId(id as string),
    queryFn: async () => apiClient.get(`${generatePath(API_ROUTES.pendingIssues.base)}?${query}`),
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
    enabled: mergeEnabled,
  });
};

export const useIsIssueResolvedQuery = (issueId: IssueId, options: { enabled?: boolean } = {}) => {
  const mergeEnabled = options?.enabled !== undefined ? options.enabled && !!issueId : !!issueId;
  return useQuery<{ resolved: boolean }>({
    queryKey: issueKeys.resolvedById(issueId),
    queryFn: async () => apiClient.get(`/api/issues/${issueId}/resolved`),
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
    enabled: mergeEnabled,
  });
};

export const useIssueActionResolvedQuery = (issueId: IssueId, actionPrefixId: PrefixId, options: { enabled?: boolean } = {}) => {
  const mergeEnabled = options?.enabled !== undefined ? options.enabled && !!issueId && !!actionPrefixId : !!issueId && !!actionPrefixId;
  return useQuery<{ resolved: boolean }>({
    queryKey: issueKeys.actionResolved(issueId, actionPrefixId),
    queryFn: async () => apiClient.get(`/api/issues/${issueId}/actions/${actionPrefixId}/resolved`, undefined, "no-cache"),
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
    enabled: mergeEnabled,
  });
};

export const useActionIssuesResolvedQuery = (actionPrefixId: PrefixId | undefined, options: { enabled?: boolean } = {}) => {
  const mergeEnabled = options?.enabled !== undefined ? options.enabled && !!actionPrefixId : !!actionPrefixId;
  return useQuery<ActionIssuesResolvedResponse>({
    queryKey: issueKeys.resolvedByActionPrefixId(actionPrefixId || ""),
    queryFn: async () => actionIssuesResolvedBatcher.fetch(actionPrefixId!),
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
    enabled: mergeEnabled,
  });
};
